import React, { memo, useState } from 'react';
import * as Styles from './QuickAccess.styles';
import { useDispatch, useSelector } from 'react-redux';
import useLocale from '@/hooks/useLocale';
import { useIsMobile } from '@/hooks/useIsMobile';

import { vpsOciActions } from '@/redux/modules/productDetail/vpsOci';

import {
  getIconBySlug, checkExistSlug, hasSlugAction, executeSlugAction,
} from '@/pages/Products/VpsOciDetailPage/utils';
import { IconConsole, IconEllipsisV3, Tooltip } from 'gatorcomponents';
import { HgProducts } from '@/components/SupportForms/SupportForms.types';

const QuickAccess = memo(({
  locale: serverListLocale = {},
  applications = [],
  packageid,
  productId,
  serverIp,
  domain,
}) => {
  const dispatch = useDispatch();
  const { productDetails: productDetailsLocale } = useLocale();
  const { vpsOci: vpsOciLocale } = productDetailsLocale;
  const n8nState = useSelector(state => state.products?.n8n);
  const [showDropdown, setShowDropdown] = useState(false);
  const isMobile = useIsMobile();

  const isDedicatedVps = !HgProducts.VPS_OCI.includes(packageid);

  const filteredApplications = applications.filter(slug => !checkExistSlug(slug, vpsOciLocale));
  const appsToShow = isMobile ? filteredApplications : filteredApplications.slice(0, 1);
  const remainingApps = isMobile ? [] : filteredApplications.slice(1);
  const hasMoreApps = remainingApps.length > 0;

  const handleOpenConsole = (serverId) => {
    dispatch(vpsOciActions.openSshTerminal.request(serverId));
  };

  const handleSlugClick = (slug) => {
    executeSlugAction(slug, {
      productId,
      serverIp,
      domain,
      onGoToTemplatesPage: null,
      onGoToSshKeysPage: null,
      n8nState,
      dispatch,
    });
  };

  const handleMouseEnterDropdown = () => {
    setShowDropdown(true);
  };

  const handleMouseLeaveDropdown = () => {
    setShowDropdown(false);
  };

  const getApplicationName = (slug) => {
    const item = vpsOciLocale?.management?.items?.find(itemLocale => itemLocale.slug === slug);
    return item ? item.name : slug;
  };

  const renderApplicationBox = (slug) => {
    const iconItem = getIconBySlug(slug);
    const itemHasHandler = hasSlugAction(slug);
    if (!iconItem) return null;

    return (
      <Tooltip content={getApplicationName(slug)} preferredPosition="bottomRight">
        <Styles.ApplicationBox
          key={slug}
          $itemHasHandler={itemHasHandler}
          onClick={itemHasHandler ? () => handleSlugClick(slug) : undefined}
        >
          <Styles.ApplicationIcon src={iconItem} alt={slug} data-testid={`quick-access-icon-${slug}`} />
        </Styles.ApplicationBox>
      </Tooltip>
    );
  };

  return (
    <Styles.QuickAccessWrapper>
      {!isDedicatedVps && (
        <Tooltip content={serverListLocale?.tooltip?.console} preferredPosition="bottomRight">
          <Styles.ApplicationBox $itemHasHandler onClick={() => handleOpenConsole(productId)} data-testid="quick-access-console">
            <IconConsole color="primaryMedium" size="md" />
          </Styles.ApplicationBox>
        </Tooltip>
      )}

      {appsToShow.map(renderApplicationBox)}

      {hasMoreApps && (
        <Styles.ApplicationBox
          $itemHasHandler
          $hasMoreApps={hasMoreApps}
          onMouseEnter={handleMouseEnterDropdown}
          onMouseLeave={handleMouseLeaveDropdown}
        >
          <IconEllipsisV3 color="primaryMedium" size="md" />
          {showDropdown && (
            <Styles.Dropdown>
              {remainingApps.map((slug) => {
                const iconItem = getIconBySlug(slug);
                const itemHasHandler = hasSlugAction(slug);
                const appName = getApplicationName(slug);
                if (!iconItem) return null;

                return (
                  <Styles.DropdownItem
                    key={slug}
                    $itemHasHandler={itemHasHandler}
                    onClick={itemHasHandler ? () => handleSlugClick(slug) : undefined}
                    data-testid={`dropdown-item-${slug}`}
                  >
                    <Styles.ApplicationIcon src={iconItem} alt={slug} />
                    <Styles.DropdownItemName>{appName}</Styles.DropdownItemName>
                  </Styles.DropdownItem>
                );
              })}
            </Styles.Dropdown>
          )}
        </Styles.ApplicationBox>
      )}
    </Styles.QuickAccessWrapper>
  );
});

export default QuickAccess;
